import React from 'react';
import { useNavigate } from 'react-router-dom';
import MetricCard from './MetricCard';
import WorkflowPipeline from './WorkflowPipeline';
import ExceptionCenter from './ExceptionCenter';
import PlannedModules from './PlannedModules';

export const AdminCommandCenter = ({ stats, loading, activeCentreName, can }) => {
  const navigate = useNavigate();

  const phlebo = stats?.phlebotomy || {};
  const access = stats?.accession || {};
  const patho = stats?.pathology || {};
  const approv = stats?.approval || {};
  const settle = stats?.settlement || {};
  const sec = stats?.security || {};
  const radio = stats?.radiology || {};
  const health = stats?.health || {};

  return (
    <div className="lis-command-deck admin-deck">
      {/* 1. Executive Telemetry Overview */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">EXECUTIVE METRICS</span>
            <h3 className="lis-section-title">Diagnostic & Operational Telemetry</h3>
            <span className="lis-section-sub">&bull; Live across {activeCentreName || 'All Centers'}</span>
          </div>
          <div className="lis-deck-actions">
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/roles')}
            >
              Access & Roles
            </button>
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/employees')}
            >
              Staff Directory
            </button>
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/masters/centres')}
            >
              Center Master
            </button>
          </div>
        </div>

        <div className="lis-kpi-grid">
          <MetricCard
            title="Total Registered Today"
            value={phlebo.totalPatientsToday ?? stats?.recentRegistrations?.totalCount ?? 0}
            subtitle="Patient check-ins recorded"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/registration"
            variant="info"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />

          <MetricCard
            title="Awaiting Phlebotomy"
            value={phlebo.pendingCount ?? 0}
            subtitle="Specimen collection queue"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/phlebotomy"
            variant={phlebo.pendingCount > 0 ? 'warning' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            }
          />

          <MetricCard
            title="Specimens Accessioned"
            value={access.receivedTodayCount ?? 0}
            subtitle="Central lab received"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/sample-receive"
            variant="success"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            }
          />

          <MetricCard
            title="Pathology Testing Pending"
            value={patho.pendingCount ?? 0}
            subtitle="Observation worklist pending"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/pathology-results"
            variant={patho.pendingCount > 0 ? 'warning' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            }
          />

          <MetricCard
            title="Critical Panic Alerts"
            value={(patho.criticalTodayCount || 0) + (approv.totalCritical || 0)}
            subtitle="Life-threatening abnormal flags"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            routeParams={{ status: 'Critical' }}
            variant={(patho.criticalTodayCount || 0) + (approv.totalCritical || 0) > 0 ? 'danger' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            }
          />

          <MetricCard
            title="Reports Awaiting Sign-Off"
            value={approv.totalPending ?? 0}
            subtitle="Pathologist validation queue"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            variant={approv.totalPending > 0 ? 'warning' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            }
          />

          <MetricCard
            title="Reports Approved Today"
            value={approv.totalApprovedToday ?? 0}
            subtitle="Released & digitally certified"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            variant="success"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            }
          />

          <MetricCard
            title="Outstanding Due Bills"
            value={`₹${Number(settle.totalDueAmount || 0).toLocaleString('en-IN')}`}
            subtitle={`${settle.unpaidCount || 0} unpaid patient orders`}
            status={loading ? 'LOADING' : 'LIVE'}
            route="/settlement"
            variant="purple"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            }
          />
        </div>
      </div>

      {/* 2. Visual Workflow Pipeline */}
      <WorkflowPipeline stats={stats} loading={loading} />

      {/* 3. Operational Exception Command Deck */}
      <ExceptionCenter stats={stats} loading={loading} />

      {/* 4. Departmental Workload & Throughput Intelligence */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">DEPARTMENT WORKLOAD</span>
            <h3 className="lis-section-title">Clinical Capacity & Queues</h3>
          </div>
        </div>

        <div className="lis-workload-grid">
          <div className="lis-workload-card" onClick={() => navigate('/pathology-results')} role="button" tabIndex={0}>
            <div className="lis-workload-header">
              <span className="lis-workload-name">Clinical Pathology & Biochemistry</span>
              <span className="lis-status-badge badge-live">OPERATIONAL</span>
            </div>
            <div className="lis-workload-counts">
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Pending Entry</span>
                <span className="lis-val highlight-amber">{patho.pendingCount ?? 0}</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Draft In-Progress</span>
                <span className="lis-val">{patho.draftCount ?? 0}</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Critical Alerts</span>
                <span className="lis-val highlight-red">{patho.criticalTodayCount ?? 0}</span>
              </div>
            </div>
            <div className="lis-workload-footer">
              <span>Inspect Pathology Workbench &rarr;</span>
            </div>
          </div>

          <div className="lis-workload-card" onClick={() => navigate('/radiology-results')} role="button" tabIndex={0}>
            <div className="lis-workload-header">
              <span className="lis-workload-name">Diagnostic Radiology & Imaging</span>
              <span className="lis-status-badge badge-live">OPERATIONAL</span>
            </div>
            <div className="lis-workload-counts">
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Studies Pending</span>
                <span className="lis-val highlight-amber">{radio.pendingResults ?? 0}</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Draft Reports</span>
                <span className="lis-val">{radio.drafts ?? 0}</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Completed Today</span>
                <span className="lis-val highlight-teal">{radio.approvedToday ?? 0}</span>
              </div>
            </div>
            <div className="lis-workload-footer">
              <span>Inspect Radiology Workbench &rarr;</span>
            </div>
          </div>

          <div className="lis-workload-card" onClick={() => navigate('/microbiology/worklist')} role="button" tabIndex={0}>
            <div className="lis-workload-header">
              <span className="lis-workload-name">Clinical Microbiology & Culture</span>
              <span className="lis-status-badge badge-no-data">NO ACTIVE CULTURES</span>
            </div>
            <div className="lis-workload-counts">
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Active Incubation</span>
                <span className="lis-val">0</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Susceptibility (AST)</span>
                <span className="lis-val">0</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Confirmed Isolates</span>
                <span className="lis-val">0</span>
              </div>
            </div>
            <div className="lis-workload-footer">
              <span>Inspect Microbiology Worklist &rarr;</span>
            </div>
          </div>

          <div className="lis-workload-card" onClick={() => navigate('/outsource/worklist')} role="button" tabIndex={0}>
            <div className="lis-workload-header">
              <span className="lis-workload-name">Reference Lab Outsource Worklist</span>
              <span className="lis-status-badge badge-no-data">NO PENDING SAMPLES</span>
            </div>
            <div className="lis-workload-counts">
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Samples Dispatched</span>
                <span className="lis-val">{stats?.outsource?.totalOutsourced ?? 0}</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">Pending Results</span>
                <span className="lis-val">0</span>
              </div>
              <div className="lis-workload-count-item">
                <span className="lis-lbl">SLA Status</span>
                <span className="lis-val highlight-teal">Normal</span>
              </div>
            </div>
            <div className="lis-workload-footer">
              <span>Inspect Outsource Worklist &rarr;</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Enterprise Security & Architecture Telemetry */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">SYSTEM TELEMETRY</span>
            <h3 className="lis-section-title">Access Governance & Health</h3>
          </div>
        </div>

        <div className="lis-health-grid">
          <div className="lis-health-card">
            <div className="lis-health-title">Active Database Link</div>
            <div className="lis-health-status healthy">
              <span className="lis-health-dot"></span>
              <strong>{health.status === 'Healthy' ? 'MySQL Connected (Port 3333)' : 'Checking Link...'}</strong>
            </div>
            <span className="lis-health-detail">
              119 Relational tables &bull; Entity Framework Core 8.0 &bull; ASP.NET Core API
            </span>
          </div>

          <div className="lis-health-card">
            <div className="lis-health-title">Configured User Accounts</div>
            <div className="lis-health-number">{sec.totalUsers ?? 39}</div>
            <span className="lis-health-detail">
              {sec.totalRoles ?? 9} Security Roles &bull; {sec.totalWorkspaces ?? 62} Centre-Role Bindings
            </span>
          </div>

          <div className="lis-health-card">
            <div className="lis-health-title">Processing Laboratory Centres</div>
            <div className="lis-health-number">{sec.totalCentres ?? 8}</div>
            <span className="lis-health-detail">
              Multi-tenant branch isolation &bull; Center-aware access control enforced
            </span>
          </div>

          <div className="lis-health-card">
            <div className="lis-health-title">Doctor Digital Signature Security</div>
            <div className="lis-health-number">PKI Bound</div>
            <span className="lis-health-detail">
              NABL accredited cryptographic signature stamping on final reports
            </span>
          </div>
        </div>
      </div>

      {/* 6. Planned / Unconfigured Enterprise Capabilities */}
      <PlannedModules />
    </div>
  );
};

export default AdminCommandCenter;
